// app/layouts/main-layout.tsx
import { ReactNode, useState, Suspense, useEffect, useMemo } from "react";
import Sidebar from "~/components/business/sidebar";
import Header from "~/components/business/header";
import { menuItems } from "~/config/customerMenu";
import { ContentLoadingOverlay } from "~/components/ContentLoadingOverlay";
import { useLoading } from "~/contexts/LoadingContext";
import { useNavigation, useLocation } from "@remix-run/react";
import { TabProvider, useTabs } from "~/contexts/TabContext";
import TabBar from "~/components/business/tabBar";
import "~/styles/layout.less";

// 客户端渲染包装器
function ClientOnly({ children }: { children: ReactNode }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  return mounted ? <>{children}</> : null;
}

const labelMap: Record<string, string> = {};
function buildLabelMap() {
  function flatten(items: any[]) {
    for (const item of items) {
      if (item.path) labelMap[item.path] = item.label;
      if (item.children) flatten(item.children);
    }
  }
  flatten(menuItems);
}
buildLabelMap();

const extraLabels: Record<string, string> = {
  "/pages/contracts/create": "创建合同",
  "/admin/business-model/task/create": "新建任务流",
  "/admin/business-model/plan/create": "新建计划模板",
};

function getPageLabel(path: string): string {
  const exact = labelMap[path] || extraLabels[path];
  if (exact) return exact;
  if (path.startsWith("/pages/opportunities/plans/")) return "计划详情";
  if (path.startsWith("/admin/business-model/task/")) return "任务流配置";
  if (path.startsWith("/admin/business-model/plan/")) return "计划模板";
  return path.split("/").pop() || path;
}

function TabAutoAdd() {
  const location = useLocation();
  const { addTab, closeAllTabs } = useTabs();
  useEffect(() => {
    const path = location.pathname;
    if (path !== "/pages/blank") {
      addTab(path, getPageLabel(path));
    } else {
      closeAllTabs();
    }
  }, [location.pathname]);
  return null;
}

function PageContent({ children }: { children: ReactNode }) {
  const { tabs } = useTabs();
  if (tabs.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          minHeight: 300,
          color: "#999",
          fontSize: 14,
        }}
      >
        暂无打开的页面，请从侧边栏选择
      </div>
    );
  }
  return <>{children}</>;
}

// 主布局组件
export default function MainLayout({
  children,
  userInfos,
  menuKey,
}: {
  children?: ReactNode;
  userInfos?: any;
  menuKey?: "admin" | "client";
}) {
  const [collapsed, setCollapsed] = useState(false);
  const { isLoading, setLoading } = useLoading();
  const sidebarWidth = collapsed ? 80 : 200;
  const navigation = useNavigation();
  const [menuKeys, setMenuKeys] = useState(menuKey);

  // 使用 useMemo 缓存 userInfos 对象，避免每次渲染都创建新对象
  const memoizedUserInfos = useMemo(() => {
    return {
      ...(userInfos || {}),
      permissionCodes: userInfos?.permissionCodes || (userInfos?.permissionCodeMap ? Object.keys(userInfos.permissionCodeMap) : [])
    };
  }, [userInfos]);

  // 添加调试
  useEffect(() => {
      const windowLocationHref = window.location.href;
     
      if (windowLocationHref.includes("/pages/")) {
        setMenuKeys("client");
      }
      if (windowLocationHref.includes("/admin/")) {
        setMenuKeys("admin");
      }
  }, []);

  // 当 Remix 有路由/loader 在进行时，开启 loading
  useEffect(() => {
    const routeLoading = navigation.state !== "idle";
    setLoading(routeLoading);
  }, [navigation.state, setLoading]);

  return (
    <ClientOnly>
      <TabProvider>
        <TabAutoAdd />
        <div className="main-layout">
          {/* 头部导航 */}
          <div className="main-header">
            <Header userInfos={memoizedUserInfos} menuKey={menuKey} />
          </div>

          {/* 主体内容 */}
          <div className="main-content-wrapper" style={{ display: "flex" }}>
            {/* 侧边栏 */}
            <div
              className="sidebar"
              style={{
                width: sidebarWidth,
              }}
            >
              <Sidebar
                menuKey={menuKeys}
                collapsed={collapsed}
                onCollapse={setCollapsed}
                menuItems={
                  (menuItems || []).find((item) => item?.key === menuKeys)
                    ?.children || []
                }
                userInfos={userInfos}
              />
            </div>

            {/* 主内容区 */}
            <div
              className="main-content"
              style={{
                width: `calc(100% - ${sidebarWidth}px)`,
                position: "relative",
              }}
            >
              <TabBar />
              <Suspense fallback={<div>加载中...</div>}>
                <PageContent>{children}</PageContent>
              </Suspense>
            </div>
          </div>
        </div>
        <ContentLoadingOverlay isLoading={isLoading} collapsed={collapsed} />
      </TabProvider>
    </ClientOnly>
  );
}
